import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Form, Input as AntDInput } from 'antd'
// eslint-disable-next-line import/extensions
import PhoneInput from 'react-phone-number-input/input'
import { useField } from 'formik'

const { Item } = Form

const PhoneInputComponent = React.forwardRef(
  ({ onChange, value, ...rest }, ref) => {
    const inputRef = React.useRef(null)

    React.useImperativeHandle(ref, () => inputRef.current?.input, [])

    const handleChange = useCallback(
      e => {
        onChange(e)
      },
      [onChange],
    )

    return (
      <AntDInput
        ref={inputRef}
        value={value}
        onChange={handleChange}
        placeholder={rest.placeholder}
        disabled={rest.disabled}
        autoComplete={rest.autoComplete}
      />
    )
  },
)

PhoneInputComponent.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  autoComplete: PropTypes.string,
}

PhoneInputComponent.defaultProps = {
  value: '',
  placeholder: undefined,
  disabled: false,
  autoComplete: undefined,
}

export default function PhoneNumber({ id, label, ...rest }) {
  const [field, meta, { setValue }] = useField(rest)
  const hasError = Boolean(meta.touched && meta.error)

  // Normalize the phone number value for the input component
  // If the value doesn't start with +, add +1 (US default)
  const normalizedValue = useMemo(() => {
    if (!field.value) return field.value
    const trimmed = String(field.value).trim()
    if (trimmed.startsWith('+')) return trimmed
    // Add +1 for US numbers without country code
    return `+1${trimmed}`
  }, [field.value])

  return (
    <Item
      label={label}
      htmlFor={id}
      hasFeedback={hasError}
      validateStatus={hasError ? 'error' : ''}
      help={hasError && meta.error}
    >
      <PhoneInput
        defaultCountry="US"
        {...field} // eslint-disable-line react/jsx-props-no-spreading
        value={normalizedValue}
        onChange={setValue}
        inputComponent={PhoneInputComponent}
        {...rest} // eslint-disable-line react/jsx-props-no-spreading
      />
    </Item>
  )
}

PhoneNumber.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
}

PhoneNumber.defaultProps = {
  label: undefined,
  id: undefined,
  type: undefined,
}
